import {
  CART_SYNC_BACKOFF_BASE_MS,
  CART_SYNC_BACKOFF_MAX_MS,
  CART_SYNC_DEBOUNCE_MS,
} from './cart-sync.constants';
import { postCartSync, postCartSyncKeepalive } from './cart-sync-api';
import type { CartSyncPayload } from './cart-sync.types';

type GetPayload = () => CartSyncPayload;
type IsLoggedIn = () => boolean;
/** When false, skip sync (e.g. server cart merged into React state not committed yet) */
type CanFlush = () => boolean;

function jitter(ms: number): number {
  let unit = 0.5;
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    unit = buf[0] / 0xffffffff;
  }
  return ms + Math.floor(unit * 500);
}

export function createCartSyncQueue(options: {
  getPayload: GetPayload;
  isLoggedIn: IsLoggedIn;
  /** Defaults to always true */
  canFlush?: CanFlush;
  debounceMs?: number;
}) {
  const debounceMs = options.debounceMs ?? CART_SYNC_DEBOUNCE_MS;
  const canFlush = options.canFlush ?? (() => true);
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let failCount = 0;
  let backoffUntil = 0;
  let inFlight = false;

  async function runSync(): Promise<void> {
    if (!options.isLoggedIn() || inFlight) return;
    if (!canFlush()) return;
    if (Date.now() < backoffUntil) return;

    inFlight = true;
    try {
      const payload = options.getPayload();
      /** Never POST empty snapshot — API replaces the whole cart and would wipe another device's lines */
      if (!payload.items?.length) {
        return;
      }
      await postCartSync(payload);
      failCount = 0;
      backoffUntil = 0;
    } catch {
      failCount += 1;
      const exp = Math.min(
        CART_SYNC_BACKOFF_MAX_MS,
        CART_SYNC_BACKOFF_BASE_MS * 2 ** Math.min(failCount, 8),
      );
      backoffUntil = Date.now() + jitter(exp);
    } finally {
      inFlight = false;
    }
  }

  function cancelPendingFlush(): void {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }

  function scheduleFlush(): void {
    if (!options.isLoggedIn() || !canFlush()) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void runSync();
    }, debounceMs);
  }

  function flushNow(): void {
    if (!canFlush()) return;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    void runSync();
  }

  function flushKeepalive(): void {
    if (!options.isLoggedIn() || !canFlush()) return;
    try {
      const payload = options.getPayload();
      if (!payload.items?.length) return;
      postCartSyncKeepalive(payload);
    } catch {
      /* silent */
    }
  }

  return {
    scheduleFlush,
    flushNow,
    flushKeepalive,
    cancelPendingFlush,
  };
}
